// The trial / licence alerts in the app bar's message area (TGCLOUD-1844).
//
//   Trial · 12 days left — contact us to keep your workspace      [Contact us]
//   Trial ended · read-only until 22 Nov — your data is safe      [Contact us]
//   This deployment is using more user accounts than its licence allows.   (every staff user, D19)
//   Licence expires on 30 Sep / has lapsed / not installed           (System Admins)
//   Migration preparation workspace: holds no patient records   [Open the workbench]  (TGCLOUD-1890)
//
// It also turns a 403 from the group gate (routes/api.js) into a one-line
// explanation, so a read-only user who presses Save learns why nothing
// happened instead of seeing "An error has occurred".

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Button, Snackbar, Stack } from '@mui/material';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { AppContext, UserContext } from 'src/store/ContextStore';
import { canUseWorkbench } from 'src/components/settings/migration-preparation/workbenchAccess';
import { useInstantFormatter } from './licenceDates';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';

export const GROUP_ACCESS_EVENT = 'tg:group-access';
const POLL_MS = 10 * 60 * 1000;

// What the group gate's 403 means, in the words the server uses (services/licence/groupAccess.js).
export function deniedNotice(groupAccess) {
  if (groupAccess === 'locked') {
    return translate('This workspace is locked. Please contact TrakGene.');
  }
  if (groupAccess === 'preparation') {
    return translate(
      'This is a migration preparation workspace: it holds no patient records, so this change is not available here.'
    );
  }
  return translate(
    'This workspace is read-only, so changes cannot be saved. Your data is safe and can still be viewed and exported.'
  );
}

export default function LicenceBanner() {
  const t = useAutoTranslation();

  const { user } = React.useContext(UserContext);
  const systemFeatures =
    React.useContext(AppContext)?.content?.settings?.systemFeatures;
  const fmt = useInstantFormatter();
  const [status, setStatus] = React.useState(null);
  const [notice, setNotice] = React.useState(null);

  const isStaff =
    !!user?.email && user?.userType !== CONSTANTS.userTypes.PATIENT;
  const isAdmin = user?.userType === CONSTANTS.userTypes.ADMIN;

  React.useEffect(() => {
    if (!isStaff) return undefined;
    let alive = true;
    const load = () =>
      axios
        .get(APICONSTANTS.licenceStatus)
        .then((res) => alive && setStatus(res.data))
        .catch(() => {});
    load();
    const t = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [isStaff, user?.userGroup]);

  React.useEffect(() => {
    const onDenied = (e) => {
      const d = e.detail || {};
      setNotice(deniedNotice(d.groupAccess));
    };
    window.addEventListener(GROUP_ACCESS_EVENT, onDenied);
    return () => window.removeEventListener(GROUP_ACCESS_EVENT, onDenied);
  }, []);

  if (!isStaff) return null;

  const group = status?.group || user?.licence || {};
  const deployment = status?.deployment || {};
  const contact = status?.group?.contactUrl;
  const contactBtn = contact ? (
    <Button
      color="inherit"
      size="small"
      href={contact}
      target="_blank"
      rel="noopener noreferrer"
    >
      {t('Contact us')}
    </Button>
  ) : null;

  const strips = [];
  if (group.plan === 'trial' && group.state === 'full') {
    strips.push(
      <Alert
        key="trial"
        severity={group.daysLeft <= 7 ? 'warning' : 'info'}
        action={contactBtn}
      >
        {t('Trial ·')} {group.daysLeft}{' '}
        {group.daysLeft === 1 ? t('day') : t('days')} {t('left (ends')}{' '}
        {fmt(group.trialEndsAt)}
        {t(') — contact us to keep your workspace.')}
      </Alert>
    );
  }
  if (group.state === 'readOnly') {
    strips.push(
      <Alert key="ro" severity="warning" action={contactBtn}>
        {t(
          'Trial ended · read-only until {{fmt}}. Your data is safe and can still be viewed and exported.',
          { fmt: fmt(group.lockAt) }
        )}
      </Alert>
    );
  }
  // A migration preparation tenant (TGCLOUD-1890): where a site's Desktop lists are mapped before
  // it migrates. The server refuses every clinical write here.
  if (group.state === 'preparation') {
    strips.push(
      <Alert
        key="prep"
        severity="info"
        action={
          canUseWorkbench(user, systemFeatures) ? (
            <Button
              color="inherit"
              size="small"
              component={RouterLink}
              to={CONSTANTS.migrationPreparationPath}
            >
              {t('Open the workbench')}
            </Button>
          ) : null
        }
      >
        {t(
          "Migration preparation workspace. It holds no patient records: here a site's TrakGene Desktop lists are mapped before the site migrates."
        )}
      </Alert>
    );
  }
  if (deployment.overPool) {
    strips.push(
      <Alert key="pool" severity="error">
        {t(
          'This TrakGene deployment is using more user accounts than its licence allows — contact your administrator.'
        )}
      </Alert>
    );
  }
  if (isAdmin && !deployment.hosted) {
    const s = deployment.status;
    const where = t('Install the new key in Settings → Licence.');
    if (s === 'expiring') {
      strips.push(
        <Alert key="lic" severity="info">
          {t('The TrakGene licence for this deployment expires in')}{' '}
          {deployment.daysLeft} {t('days.')} {where}
        </Alert>
      );
    } else if (s === 'expired') {
      strips.push(
        <Alert key="lic" severity="error">
          {t('The TrakGene licence has expired. No users can be added from')}{' '}
          {fmt(deployment.freezeAt)}. {where}
        </Alert>
      );
    } else if (
      s === 'lapsed' ||
      s === 'missing' ||
      s === 'invalid' ||
      s === 'wrongDomain'
    ) {
      strips.push(
        <Alert key="lic" severity={deployment.frozen ? 'error' : 'warning'}>
          {s === 'lapsed' && `${t('The TrakGene licence has lapsed.')} `}
          {s === 'missing' && `${t('No TrakGene licence is installed.')} `}
          {s === 'invalid' &&
            `${t('The installed TrakGene licence key is not valid.')} `}
          {s === 'wrongDomain' &&
            `${t(
              'The installed TrakGene licence is for a different address.'
            )} `}
          {deployment.frozen
            ? t(
                'No users can be added or reactivated until a valid licence is installed. Existing users are not affected.'
              )
            : t('Users can still be added until {{fmt}}.', {
                fmt: fmt(deployment.freezeAt)
              })}{' '}
          {where}
        </Alert>
      );
    }
  }

  // Same look as the system announcements above it (standard MUI Alert,
  // rounded) and the same 8px stack, so any number of them sit one under the
  // other instead of on top of each other.
  return (
    <>
      {strips.length > 0 && (
        <Stack spacing={1} sx={{ width: '100%' }}>
          {strips}
        </Stack>
      )}
      <Snackbar
        open={!!notice}
        autoHideDuration={8000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          onClose={() => setNotice(null)}
          variant="filled"
        >
          {notice}
        </Alert>
      </Snackbar>
    </>
  );
}
